import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  MapPin,
  Phone,
  Clock,
  Star,
  MessageCircle,
  Menu,
  X,
  ChevronDown,
  Utensils,
  Coffee,
  Wine,
  Cake,
  Facebook,
  Instagram,
} from "lucide-react";

// Reviews
const reviews = [
  {
    name: "María González",
    rating: 5,
    text: "¡Como si estuviera en la casa de mi abuela! Los casados son increíbles y el servicio es muy cálido. Definitivamente volveré.",
    date: "Hace 2 semanas",
  },
  {
    name: "Carlos Rodríguez",
    rating: 5,
    text: "El mejor gallo pinto de San José. El ambiente es muy acogedor y los precios son muy razonables para la calidad que ofrecen.",
    date: "Hace 1 mes",
  },
  {
    name: "Ana Vargas",
    rating: 4,
    text: "Excelente comida típica. La gallina achiotada es espectacular. Un lugar perfecto para llevar a la familia.",
    date: "Hace 3 semanas",
  },
];

// Gallery images
const galleryImages = [
  { src: "/galeria/1.jpeg", alt: "Galería 1" },
  { src: "/galeria/2.jpeg", alt: "Galería 2" },
  { src: "/galeria/3.jpeg", alt: "Galería 3" },
  { src: "/galeria/4.jpeg", alt: "Galería 4" },
  { src: "/galeria/5.jpeg", alt: "Galería 5" },
  { src: "/galeria/6.jpeg", alt: "Galería 6" },
  { src: "/galeria/7.jpeg", alt: "Galería 7" },
  { src: "/galeria/8.jpeg", alt: "Galería 8" },
  { src: "/galeria/9.jpeg", alt: "Galería 9" },
];

// Star Rating Component
const StarRating = ({ rating }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        className={`w-5 h-5 ${star <= rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`}
      />
    ))}
  </div>
);

// Navigation
const Navigation = ({ mobileMenuOpen, setMobileMenuOpen }) => {
  const navLinks = [
    { href: "#inicio", label: "Inicio" },
    { href: "#nosotros", label: "Nosotros" },
    { href: "#menu", label: "Menú" },
    { href: "#galeria", label: "Galería" },
    { href: "#ubicacion", label: "Ubicación" },
    { href: "#resenas", label: "Reseñas" },
  ];

  return (
    <nav
      className="sticky top-0 z-50 w-full border-b border-border/40 bg-white/95 backdrop-blur-md"
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a
            href="#inicio"
            className="flex items-center gap-2"
            data-testid="nav-logo"
          >
            <img
              src="/images/logoactualizado.png"
              alt="La Esquinita de la Abuela"
              className="h-12 md:h-14 w-auto"
            />
          </a>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="nav-link text-foreground/80 hover:text-primary font-medium transition-colors"
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          <div className="hidden md:block">
            <Button
              className="bg-secondary text-white hover:bg-secondary/90 rounded-full px-6"
              data-testid="nav-reservar-btn"
              onClick={() =>
                window.open(
                  "https://wa.me/50671089178?text=Hola,%20quisiera%20hacer%20una%20reservación",
                  "_blank",
                )
              }
            >
              Reservar
            </Button>
          </div>

          <button
            className="md:hidden p-2 rounded-lg hover:bg-muted"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border/40">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-foreground/80 hover:text-primary font-medium py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
              <Button
                className="bg-secondary text-white hover:bg-secondary/90 rounded-full w-full mt-2"
                onClick={() =>
                  window.open("https://wa.me/50671089178", "_blank")
                }
              >
                Reservar
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => (
  <section
    id="inicio"
    className="relative min-h-[90vh] flex items-center justify-center overflow-hidden"
    data-testid="hero-section"
  >
    <div
      className="absolute inset-0 bg-cover bg-center"
      style={{
        backgroundImage: `url('https://customer-assets.emergentagent.com/job_171d6c05-d45a-4338-b4c9-31b92b6894ef/artifacts/s0uqh7it_image.png')`,
      }}
    />
    <div className="hero-overlay absolute inset-0" />
<link rel="icon" type="image/png" href="/favicon.png" />
    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <div className="animate-fadeInUp">
        <p className="font-accent text-2xl md:text-3xl text-white/90 mb-4">
          Bienvenidos a
        </p>
        <img
          src="/images/logoactualizado.png"
          alt="La Esquinita de la Abuela"
          className="h-40 md:h-56 lg:h-64 w-auto mx-auto mb-6 drop-shadow-2xl"
        />
      </div>

      <p className="animate-fadeInUp animate-delay-200 text-lg md:text-xl text-white/90 max-w-2xl mx-auto mb-8">
        Cocina a la leña • Sabores tradicionales costarricenses con el amor de
        la abuela
      </p>

      <div className="animate-fadeInUp animate-delay-300 flex flex-col sm:flex-row gap-4 justify-center">
        <Button
          size="lg"
          className="bg-secondary text-white hover:bg-secondary/90 rounded-full px-8 py-6 text-lg shadow-lg"
          data-testid="hero-menu-btn"
          onClick={() =>
            document
              .getElementById("menu")
              .scrollIntoView({ behavior: "smooth" })
          }
        >
          Ver Menú
        </Button>
        <Button
          size="lg"
          variant="outline"
          className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg"
          data-testid="hero-ubicacion-btn"
          onClick={() =>
            document
              .getElementById("ubicacion")
              .scrollIntoView({ behavior: "smooth" })
          }
        >
          <MapPin className="w-5 h-5 mr-2" />
          Cómo Llegar
        </Button>
      </div>

      <div className="animate-fadeInUp animate-delay-400 mt-12 inline-flex items-center gap-3 bg-white/20 backdrop-blur-sm rounded-full px-6 py-3">
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star
              key={star}
              className={`w-5 h-5 ${star <= 4.5 ? "fill-yellow-400 text-yellow-400" : "fill-yellow-400/50 text-yellow-400/50"}`}
            />
          ))}
        </div>
        <span className="text-white font-medium">4.5 en Google</span>
        <span className="text-white/80">• +700 reseñas</span>
      </div>

      <div className="animate-fadeInUp animate-delay-500 mt-6 flex justify-center gap-4 flex-wrap">
        <span className="bg-white/90 text-foreground px-4 py-2 rounded-full text-sm font-medium">
          🛵 Uber Eats
        </span>
        <span className="bg-white/90 text-foreground px-4 py-2 rounded-full text-sm font-medium">
          🛵 Rappi
        </span>
        <span className="bg-white/90 text-foreground px-4 py-2 rounded-full text-sm font-medium">
          🛵 DiDi Food
        </span>
      </div>
    </div>

    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
      <ChevronDown className="w-8 h-8 text-white/60" />
    </div>
  </section>
);

// About Section
const AboutSection = () => (
  <section
    id="nosotros"
    className="py-16 md:py-24 bg-white"
    data-testid="about-section"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-2 gap-12 items-center">
        <div className="relative">
          <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
            <img
              src="https://customer-assets.emergentagent.com/job_171d6c05-d45a-4338-b4c9-31b92b6894ef/artifacts/s0uqh7it_image.png"
              alt="Local"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-secondary/20 rounded-2xl -z-10" />
          <div className="absolute -top-6 -left-6 w-24 h-24 bg-primary/20 rounded-2xl -z-10" />
        </div>

        <div>
          <p className="font-accent text-2xl text-secondary mb-2">
            Nuestra Historia
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-6">
            Una tradición familiar
          </h2>
          <div className="space-y-4 text-muted-foreground text-lg leading-relaxed">
            <p>
              En{" "}
              <strong className="text-primary">
                La Esquinita de la Abuela
              </strong>
              , cada plato cuenta una historia. Somos un restaurante familiar
              ubicado en el corazón de San José, dedicado a preservar los
              sabores auténticos de la cocina costarricense.
            </p>
            <p>
              Nuestra{" "}
              <strong className="text-secondary">cocina a la leña</strong> y
              nuestras recetas han pasado de generación en generación,
              manteniendo viva la tradición culinaria tica.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Clock className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="font-medium text-foreground">Horario</p>
                <p className="text-sm text-muted-foreground">
                  6:30 AM - 10:00 PM
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center">
                <Utensils className="w-6 h-6 text-secondary" />
              </div>
              <div>
                <p className="font-medium text-foreground">Cocina a la Leña</p>
                <p className="text-sm text-muted-foreground">
                  100% Tradicional
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Menu Section
const MenuSection = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  const platos = [
    "/menu/1.jpg",
    "/menu/2.jpg",
    "/menu/3.jpg",
    "/menu/4.jpg",
    "/menu/5.jpg",
    "/menu/6.jpg",
    "/menu/7.jpg",
    "/menu/8.jpg",
  ];

  return (
    <section id="menu" className="py-16 md:py-24 bg-[#F5F8FA]">
      <div className="max-w-7xl mx-auto px-4">
        {/* HEADER */}
        <div className="text-center mb-12">
          <p className="font-accent text-2xl text-secondary mb-2">
            Nuestro Menú
          </p>
          <h2 className="text-4xl md:text-5xl font-bold">
            Sabores de <span className="text-accent-blue">Costa Rica</span>
          </h2>
        </div>

        {/* PORTADA (CLICK → PDF) */}
        <a
          href="/menu.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="block group mb-16"
        >
          <div className="relative rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition">
            <img
              src="/images/portada.jpeg"
              alt="Menú"
              className="w-full h-[400px] object-cover group-hover:scale-105 transition duration-500"
            />

            <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center text-white text-center">
              <h3 className="text-3xl md:text-4xl font-bold mb-4">
                Ver Menú Completo
              </h3>

              <div className="flex gap-4 flex-wrap justify-center">
                <a
                  href="/menu.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-secondary px-6 py-3 rounded-full font-semibold"
                >
                  📖 Ver Menú
                </a>

                <a
                  href="/menu.pdf"
                  download
                  className="border-2 border-white px-6 py-3 rounded-full font-semibold"
                >
                  ⬇ Descargar
                </a>
              </div>
            </div>
          </div>
        </a>

        {/* PLATOS DESTACADOS */}
        <div className="mt-20">
          <div className="text-center mb-10">
            <h3 className="text-3xl font-bold">
              Nuestros <span className="text-secondary">Platos</span>
            </h3>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {platos.map((src, i) => (
              <div
                key={i}
                onClick={() => setSelectedImage(src)}
                className="relative group cursor-pointer rounded-xl overflow-hidden shadow-lg"
              >
                <img
                  src={src}
                  alt={`Plato ${i + 1}`}
                  className="w-full h-56 object-cover group-hover:scale-110 transition duration-500"
                />

                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition">
                  <span className="text-white text-sm font-semibold">
                    Ver plato
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* BOTÓN EXTRA */}
          <div className="text-center mt-10">
            <a
              href="/menu.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-3 bg-accent-blue text-white rounded-full font-semibold hover:opacity-90 transition"
            >
              Ver Menú Completo
            </a>
          </div>
        </div>

        {/* MODAL */}
        {selectedImage && (
          <div
            onClick={() => setSelectedImage(null)}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
          >
            <img
              src={selectedImage}
              alt="Plato ampliado"
              className="max-w-[90%] max-h-[90%] rounded-xl shadow-2xl"
            />
          </div>
        )}
      </div>
    </section>
  );
};

// Gallery Section
const GallerySection = () => (
  <section
    id="galeria"
    className="py-16 md:py-24"
    data-testid="gallery-section"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <p className="font-accent text-2xl text-secondary mb-2">
          Nuestra Galería
        </p>
        <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4">
          Un vistazo a nuestro restaurante
        </h2>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {galleryImages.map((image, index) => (
          <div
            key={index}
            className={`gallery-item overflow-hidden rounded-2xl shadow-lg ${index === 0 ? "md:col-span-2 md:row-span-2" : ""}`}
            data-testid={`gallery-item-${index}`}
          >
            <div className="relative aspect-square overflow-hidden">
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-full object-cover transition-transform duration-700"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Location Section
const LocationSection = () => (
  <section
    id="ubicacion"
    className="py-16 md:py-24 bg-[#F5F8FA]"
    data-testid="location-section"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-2 gap-12 items-center">
        <div>
          <p className="font-accent text-2xl text-secondary mb-2">
            Encuéntranos
          </p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-6">
            Nuestra Ubicación
          </h2>

          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-medium text-foreground text-lg">
                  Dirección
                </h3>
                <p className="text-muted-foreground">
                  Barrio Otoya, 150 mts Oeste de la rampa del Hospital Calderón
                  Guardia, San José
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-secondary" />
              </div>
              <div>
                <h3 className="font-medium text-foreground text-lg">Horario</h3>
                <p className="text-muted-foreground">
                  Lunes a Domingo: 6:30 AM - 10:00 PM
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                <Phone className="w-6 h-6 text-accent" />
              </div>
              <div>
                <h3 className="font-medium text-foreground text-lg">
                  Contacto
                </h3>
                <p className="text-muted-foreground">
                  WhatsApp: +506 7108-9178
                  <br />
                  Facebook: @LaEsquinitadelaAbuelacr
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-4 mt-8">
            <Button
              className="bg-primary text-white hover:bg-primary/90 rounded-full"
              data-testid="location-maps-btn"
              onClick={() =>
                window.open("https://maps.app.goo.gl/Wb29gJk3CHPp5cWT9")
              }
            >
              <MapPin className="w-4 h-4 mr-2" />
              Google Maps
            </Button>
            <Button
              variant="outline"
              className="border-2 border-secondary text-secondary hover:bg-secondary/10 rounded-full"
              onClick={() => window.open("https://wa.me/50671089178", "_blank")}
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp
            </Button>
          </div>
        </div>

        <div className="map-container aspect-square md:aspect-[4/3]">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3929.954057437664!2d-84.07388552612161!3d9.937780774104587!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8fa0e31d11147b45%3A0xd5853b424c675e6f!2sLa%20Esquinita%20de%20la%20Abuela!5e0!3m2!1ses!2scr!4v1777651928437!5m2!1ses!2scr"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            title="Ubicación"
          />
        </div>
      </div>
    </div>
  </section>
);

// Reviews Section
const ReviewsSection = () => (
  <section
    id="resenas"
    className="py-16 md:py-24"
    data-testid="reviews-section"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <p className="font-accent text-2xl text-secondary mb-2">Opiniones</p>
        <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-4">
          Lo que dicen nuestros clientes
        </h2>
        <div className="flex items-center justify-center gap-4 mt-6">
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className={`w-6 h-6 ${star <= 4.5 ? "fill-yellow-400 text-yellow-400" : "fill-yellow-400/50 text-yellow-400/50"}`}
              />
            ))}
          </div>
          <span className="text-2xl font-bold text-foreground">4.5</span>
          <span className="text-muted-foreground">
            • +700 reseñas en Google
          </span>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {reviews.map((review, index) => (
          <Card
            key={index}
            className="review-card border-0 shadow-lg hover:shadow-xl transition-shadow"
            data-testid={`review-card-${index}`}
          >
            <CardContent className="p-6">
              <StarRating rating={review.rating} />
              <p className="font-accent text-xl text-foreground mt-4 leading-relaxed">
                "{review.text}"
              </p>
              <div className="flex items-center gap-3 mt-6 pt-4 border-t border-border/50">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="font-medium text-primary">
                    {review.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-foreground">{review.name}</p>
                  <p className="text-sm text-muted-foreground">{review.date}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="text-center mt-12">
        <Button
          variant="outline"
          className="border-2 border-primary text-primary hover:bg-primary/10 rounded-full"
          onClick={() =>
            window.open(
              "https://www.facebook.com/LaEsquinitadelaAbuelacr",
              "_blank",
            )
          }
        >
          Ver todas las reseñas
        </Button>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer
    className="bg-primary text-white pt-16 pb-8 relative overflow-hidden"
    data-testid="footer"
  >
    {/* Glow decorativo */}
    <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
      <div className="absolute w-72 h-72 bg-accent-blue rounded-full blur-3xl top-[-50px] left-[-50px]" />
      <div className="absolute w-72 h-72 bg-secondary rounded-full blur-3xl bottom-[-50px] right-[-50px]" />
    </div>

    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      {/* GRID */}
      <div className="grid md:grid-cols-4 gap-10">
        {/* BRAND */}
        <div className="md:col-span-2">
          <img
            src="/images/logoactualizado.png"
            alt="Logo"
            className="h-16 w-auto mb-4"
          />

          <p className="text-white/80 max-w-md leading-relaxed">
            Cocina a la leña con sabores tradicionales costarricenses. El amor y
            la calidez de la cocina de la abuela.
            <span className="text-accent-blue font-semibold">
              {" "}
              ¡Te esperamos!
            </span>
          </p>

          {/* SOCIAL */}
          <div className="flex gap-4 mt-6">
            <a
              href="https://www.facebook.com/LaEsquinitadelaAbuelacr"
              target="_blank"
              rel="noopener noreferrer"
              className="w-11 h-11 rounded-full bg-white/10 hover:bg-accent-blue transition-all flex items-center justify-center hover:scale-110"
            >
              <Facebook className="w-5 h-5" />
            </a>

            <a
              href="https://www.instagram.com/laesquinitadelaabuelacr/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-11 h-11 rounded-full bg-white/10 hover:bg-accent-blue transition-all flex items-center justify-center hover:scale-110"
            >
              <Instagram className="w-5 h-5" />
            </a>
          </div>
        </div>

        {/* LINKS */}
        <div>
          <h4 className="font-heading font-bold text-lg mb-4">Navegación</h4>

          <ul className="space-y-3">
            {["Inicio", "Menú", "Galería", "Ubicación"].map((link) => (
              <li key={link}>
                <a
                  href={`#${link.toLowerCase()}`}
                  className="text-white/70 hover:text-accent-blue transition-colors flex items-center gap-2"
                >
                  <span className="w-1.5 h-1.5 bg-accent-blue rounded-full"></span>
                  {link}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* CONTACTO */}
        <div>
          <h4 className="font-heading font-bold text-lg mb-4">Contacto</h4>

          <ul className="space-y-3 text-white/70">
            <li className="flex items-center gap-3">
              <MapPin className="w-5 h-5 text-accent-blue" />
              Barrio Otoya, San José
            </li>

            <li className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-accent-blue" />
              6:30 AM - 10:00 PM
            </li>

            <li className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-accent-blue" />
              +506 7108-9178
            </li>
          </ul>

          {/* CTA MENU */}
          <a
            href="/menu.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-6 px-5 py-2 rounded-full bg-accent-blue text-white text-sm font-semibold hover:opacity-90 transition"
          >
            Ver Menú
          </a>
        </div>
      </div>

      {/* APPS */}
      <div className="border-t border-white/10 mt-12 pt-8">
        <div className="flex justify-center gap-4 flex-wrap mb-6">
          {["Uber Eats", "Rappi", "DiDi Food"].map((app) => (
            <span
              key={app}
              className="bg-white/10 px-5 py-2 rounded-full text-sm hover:bg-accent-blue/20 transition"
            >
              {app}
            </span>
          ))}
        </div>

        {/* COPYRIGHT */}
        <div className="text-center text-white/50 text-sm">
          <p>© 2026 La Esquinita de la Abuela — Hecho con 🔥 y tradición 🇨🇷</p>
        </div>
      </div>
    </div>
  </footer>
);

// WhatsApp Button
const WhatsAppButton = () => (
  <a
    href="https://wa.me/50671089178?text=Hola,%20quisiera%20información"
    target="_blank"
    rel="noopener noreferrer"
    className="fixed bottom-6 right-6 z-50 whatsapp-btn w-16 h-16 rounded-full bg-[#25D366] hover:bg-[#20BD5C] flex items-center justify-center shadow-lg transition-colors"
    data-testid="whatsapp-float-btn"
  >
    <MessageCircle className="w-8 h-8 text-white" />
  </a>
);

// Home Component
const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <Navigation
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />
      <HeroSection />
      <AboutSection />
      <MenuSection />
      <GallerySection />
      <LocationSection />
      <ReviewsSection />
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
